import { isInNewCTAExperiment } from '@client/redux/experiments/selectors/new-cta';
import { CouplesUsuallySpendEmptyDetails } from '@components/couples-usually-spend/CouplesUsuallySpendEmptyDetails';
import { useDeviceType } from '@hooks/use-device-type';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { RelatedSearches } from '@pages/Storefront/containers/lite_vendor_about/components/related_searches';
import { useAppSelector } from '@redux/hooks';
import { ReviewsCTA } from '@tkmp/rfq/components/ViewConversation/ReviewsCTA';
import { Icon, type IconName } from '@tkww/orion-web-ds-graphic';
import { GridColumn, GridRow } from '@tkww/orion-web-ds-structure';
import { Body1, Editorial } from '@xo-union/tk-ui-typography';
import { useAtomValue } from 'jotai';
import React from 'react';
import {
	couplesUsuallySpendAtom,
	formattedPricingDetailsAtom,
	hasPricingDetailsAtom,
	pricingDetailsAtom,
} from '../../../jotai/storefront/pricing';
import { CUSSecondaryCta } from './CUSSecondaryCta';
import Styles from './PricingDetails.scss';

type FAQProps = {
	className: string;
	icon: IconName;
	text: string;
	value: string;
};

const FAQ = ({ className, icon, text, value }: FAQProps) => {
	return (
		<div className={className}>
			<Icon name={icon} size="md" aria-label={undefined} aria-hidden={true} />
			<div className={Styles.text}>
				<Body1 className={Styles.faqTitle}>{text}</Body1>
				<Body1 bold>{value}</Body1>
			</div>
		</div>
	);
};

const CouplesUsuallySpend = () => {
	const usuallySpend = useAtomValue(couplesUsuallySpendAtom);
	const { peakMonths } = useAtomValue(pricingDetailsAtom);

	return (
		<ul className={Styles.usuallySpendDisclaimerList}>
			{usuallySpend && <li>Couples usually spend {usuallySpend}</li>}
			<li>
				Starting prices don't include service fees, taxes, gratuity, and rental
				fees.
			</li>
			<li>
				Guest count and seasonality may also affect prices.
				{peakMonths && ` Peak season for this venue is ${peakMonths}.`}
			</li>
		</ul>
	);
};

export const CUSPricingDetails = () => {
	const pricingDetails = useAtomValue(formattedPricingDetailsAtom);
	const hasPricingDetails = useAtomValue(hasPricingDetailsAtom);
	const couplesUsuallySpend = useAtomValue(couplesUsuallySpendAtom);
	const isLiteStorefront = useLiteStorefront();
	const inNewCTAExperiment = useAppSelector(isInNewCTAExperiment);
	const vendorName = useAppSelector((state) => state.vendor.vendor?.name) ?? '';
	const { isMobile } = useDeviceType();

	return hasPricingDetails && !isLiteStorefront ? (
		<>
			<div className={Styles.cusPricingDetails}>
				<div className={Styles.startingPrices}>
					<Editorial bold>Starting prices</Editorial>
					<GridRow>
						<GridColumn md="4" className={Styles.receptionFaqDesktop}>
							<div className={Styles.circle}>
								<Icon
									name="champagne-glass"
									size="lg"
									aria-label={undefined}
									aria-hidden={true}
								/>
							</div>
							<Body1>Reception</Body1>
							<Body1 bold>{pricingDetails.reception}</Body1>
						</GridColumn>
						<GridColumn md="8">
							<FAQ
								className={Styles.faqContainerReception}
								icon="champagne-glass"
								text="Reception"
								value={pricingDetails.reception}
							/>
							<FAQ
								className={Styles.faqContainer}
								icon="rounded-altar"
								text="Ceremony"
								value={pricingDetails.ceremony}
							/>
							<FAQ
								className={Styles.faqContainer}
								icon="glass"
								text="Bar service"
								value={pricingDetails.barService}
							/>
							<FAQ
								className={Styles.faqContainer}
								icon="cutlery"
								text="Catering"
								value="Contact for price"
							/>
						</GridColumn>
					</GridRow>
				</div>
			</div>
			<CouplesUsuallySpend />

			{inNewCTAExperiment && isMobile ? (
				<ReviewsCTA vendorName={vendorName} />
			) : (
				<CUSSecondaryCta couplesUsuallySpend={couplesUsuallySpend} />
			)}
		</>
	) : (
		<>
			<CouplesUsuallySpendEmptyDetails
				couplesUsuallySpend={couplesUsuallySpend}
			/>
			{isLiteStorefront ? (
				<RelatedSearches headline="Explore other vendors that suit your budget" />
			) : (
				<CUSSecondaryCta couplesUsuallySpend={couplesUsuallySpend} />
			)}
		</>
	);
};
